import { Config } from '../../../../video-data/config';
import type { Transcript } from '../../../Transcript';
import type { TextMetrics } from '../metrics/TextMetrics';
import { BezierCurve } from '../../../../libs/BezierCurve';
import type { DynamicLevels, DynamicInferredDimensions } from '../../../../enums';
import { DynamicInferredHeight, DynamicInferredWidth, DynamicLevel } from '../../../../enums';
import type { TextMetricItemAPI } from '../metrics/WordElementMetrics';
import type { RemoveAnchoredPoints } from '../../../../libs/Curve';
import type { AnimationConfig } from '../../../../libs/AnimationCurveManager';

export interface TextDynamicAnimationRendererSettings {
    enabled: boolean;
    property: string | null;
    level: DynamicLevels;
    duration: number;
    startValue: number | DynamicInferredDimensions;
    endValue: number | DynamicInferredDimensions;
    startMulti: number;
    endMulti: number;
    interpolation: (typeof Config.ALL_INTERPOLATION_PRESETS)[number] | null;
    zigZagPerLine: boolean;
    zigZagPerLineStart: (typeof Config.ALL_ZIG_ZAG_START_OPTIONS)[number] | null;
    shouldOffsetPropertyValue: boolean;

    // Only for animations driven by the animation manager
    staggerDelay: number;
    delay: number;
    mirrorStaggerDelay: boolean;
    anchorsToRemove: RemoveAnchoredPoints;
}

export type AnimatedValue = {
    value: number;
};

/**
 * Generates animation configurations to drive object properties.
 */
export abstract class TextDynamicAnimationBase<PropertyType extends string> {
    enabled: boolean = true;
    property: PropertyType | null = null;
    level: DynamicLevel = DynamicLevel.object;
    staggerDelay: number = 0;
    delay: number = 0;
    duration: number = 100;
    startValue: number | DynamicInferredDimensions = 0;
    endValue: number | DynamicInferredDimensions = 1;
    startMulti: number = 1;
    endMulti: number = 1;
    interpolation: (typeof Config.ALL_INTERPOLATION_PRESETS)[number] | null = null;

    /** Dynamic animation can be created for text or graphics. */
    parent = null;

    // this is only for object animation
    zigZagPerLine: boolean = false;
    zigZagPerLineStart: (typeof Config.ALL_ZIG_ZAG_START_OPTIONS)[number] | null = null;
    shouldOffsetPropertyValue: boolean = false;
    mirrorStaggerDelay: boolean = false;
    anchorsToRemove: RemoveAnchoredPoints = Config.NONE_ANIMATION_CURVE_ANCHOR;

    /**
     * Stores the computed animation values for each word element. This value gets updated
     * each frame and is used when drawing the text & graphics
     */
    computedValues: number[] = [];
    /**
     * This is an intermediate object to enable animation driven by the animation manager.
     * When animating with the animation manager, curves drive the values of this object. Since we
     * don't want to create a curve for each word, we initialize valuesToAnimate based on the level
     * the animation is on. So for object level, there will only be one entry.
     * During a frame draw, the animation curve will update valuesToAnimate and then value(s) will be
     * spread to the computedValues.
     */
    valuesToAnimate: AnimatedValue[] = [];

    apply(settings: TextDynamicAnimationRendererSettings) {
        this.enabled = settings.enabled;
        this.property = settings.property as PropertyType;
        this.level = settings.level;
        this.staggerDelay = settings.staggerDelay;
        this.delay = settings.delay;
        this.duration = settings.duration;
        this.startValue = settings.startValue;
        this.endValue = settings.endValue;
        this.startMulti = settings.startMulti;
        this.endMulti = settings.endMulti;
        this.interpolation = settings.interpolation;
        this.zigZagPerLine = settings.zigZagPerLine;
        this.zigZagPerLineStart = settings.zigZagPerLineStart;
        this.shouldOffsetPropertyValue = settings.shouldOffsetPropertyValue;
        this.mirrorStaggerDelay = settings.mirrorStaggerDelay;
        this.anchorsToRemove = settings.anchorsToRemove;
    }

    /**
     * Used to compute the current value of animated values based on the current time and the transcript.
     * This is used for animations timed to the transcript.
     */
    computeTranscriptAnimationValues(
        currentTime: number,
        transcript: Transcript,
        textMetrics: TextMetrics,
    ) {
        switch (this.level) {
            case DynamicLevel.object:
                this._computeObjectTranscriptAnimationValues(currentTime, transcript, textMetrics);
                break;
            case DynamicLevel.line:
                this._computeLineTranscriptAnimationValues(currentTime, transcript, textMetrics);
                break;
            case DynamicLevel.word:
                this._computeWordTranscriptAnimationValues(currentTime, transcript, textMetrics);
                break;
        }
    }

    /**
     * Used to initialize animations that will be driven by the animation manager (not timed to the transcript).
     */
    initializeValuesToAnimate(numberOfLevelObjects: number) {
        this.valuesToAnimate = Array.from({ length: numberOfLevelObjects }, () => ({ value: 0 }));
    }

    /**
     * Dynamically builds the animation configuration for the animation manager.
     * This is called from the "asset" side of the canvas bridge, but we want this logic here because
     * the canvas side has a different scale than the asset side. The animation curve needs to be built
     * with the same scale as the canvas side because the curve drives valuesToAnimate (which is spread to
     * computedValues). If we wanted to build the curve in the asset side, we would need to map the animated
     * values to the canvas scale on each frame draw.
     */
    buildAnimationManagerConfig(
        getInferredValues: (
            inferredDimension: DynamicInferredDimensions,
            level: DynamicLevel,
        ) => number[],
        getOffsetForProperty: (
            property: PropertyType,
            shouldOffsetPropertyValue: boolean,
            level: DynamicLevel,
        ) => number,
        containerDuration: number,
    ): AnimationConfig | undefined {
        if (!this.enabled) {
            return;
        }
        if (!this.property || this.interpolation === null || this.zigZagPerLineStart === null) {
            return;
        }
        // scale the curve to match the requested duration
        // ensure the requested duration isn't longer then half the container's duration
        const halfContainerDuration = containerDuration / 2;
        const curveDuration = Math.min(this.duration, halfContainerDuration);
        const curveScale = curveDuration / halfContainerDuration;

        // curve
        const curve = BezierCurve.fromInterpolationPreset({
            delay: this.delay,
            duration: curveDuration,
            interpolation: this.interpolation,
        })
            .scale(curveScale, true, Config.ALL_ANIMATION_CURVE_ANCHOR)
            .getCurveDescription();

        // range
        let startValue: number, endValue: number;
        if (typeof this.startValue === 'number') {
            startValue = this.startValue;
        } else {
            // Note that it's unexpected that we just take the first value here. This is because we had this
            // bug in the previous dynamic animation implementation, and we want to keep consistent behaviour
            // across the migration. We may want to fix this later, but it would impact existing designs.
            // What this means is that regardless of the level, we always take the first value. So this is what we
            // want for object level, but not for the others. For example, it means that every word animation will
            // infer the dimensions from the first word in the text.
            startValue = getInferredValues(this.startValue, this.level)[0];
        }
        startValue *= this.startMulti;

        if (typeof this.endValue === 'number') {
            endValue = this.endValue;
        } else {
            // See the comment above for why we take the first value here.
            endValue = getInferredValues(this.endValue, this.level)[0];
        }
        endValue *= this.endMulti;

        const offset = getOffsetForProperty(
            this.property,
            this.shouldOffsetPropertyValue,
            this.level,
        );
        const drivenRange = this.#applyOffsetToDrivenRange(offset, [startValue, endValue]);
        const drivenObjects = this.valuesToAnimate;
        const drivenProperty = ['value'];

        const percentageOffset = containerDuration > 0 ? this.staggerDelay / containerDuration : 0;

        return {
            name: this.property,
            mode: 'multi',
            offsetMode: 'object',
            curve,
            percentageOffset,
            shouldOffsetPropertyValue: this.shouldOffsetPropertyValue,
            mirrorPercentageOffset: this.mirrorStaggerDelay,
            drivenRange,
            drivenProperty,
            drivenObjects,
            duration: this.duration,
            removeAnchoredPoints: this.anchorsToRemove,
            zigZagPerLine: this.zigZagPerLine,
            zigZagPerLineStart: this.zigZagPerLineStart,
        };
    }

    /**
     * Applies an offset to the driven range to create the final driven range.
     */
    #applyOffsetToDrivenRange(offset: number, range: [number, number]): [number, number] {
        return [range[0] + offset, range[1] + offset];
    }

    _computeObjectTranscriptAnimationValues(
        currentTime: number,
        transcript: Transcript,
        textMetrics: TextMetrics,
    ) {
        const [startWordIndex] = textMetrics.sourceWordReferenceIndexRange;
        const progress = calculateWordProgress(
            transcript,
            startWordIndex!,
            this.duration,
            currentTime,
        );
        const computedValue = this.#computeCurrentValue(progress, textMetrics);
        this.computedValues = textMetrics.spreadLevelValues([computedValue], DynamicLevel.object);
    }

    protected _computeLineTranscriptAnimationValues(
        currentTime: number,
        transcript: Transcript,
        textMetrics: TextMetrics,
    ) {
        const elementCount = textMetrics.lines.length;
        const computedValues = Array(elementCount).fill(0);
        for (let i = 0; i < elementCount; i++) {
            const [startIndex] = textMetrics.lines[i].sourceWordReferenceIndexRange;
            const progress = calculateWordProgress(
                transcript,
                startIndex!,
                this.duration,
                currentTime,
            );
            computedValues[i] = this.#computeCurrentValue(progress, textMetrics.lines[i], i);
        }
        this.computedValues = computedValues;
    }

    protected _computeWordTranscriptAnimationValues(
        currentTime: number,
        transcript: Transcript,
        textMetrics: TextMetrics,
    ) {
        const [startWordIndex, endWordIndex] = textMetrics.sourceWordReferenceIndexRange;
        const elementCount = endWordIndex! - startWordIndex! + 1;
        const computedValues = Array(elementCount).fill(0);
        for (let i = startWordIndex!, j = 0; i <= endWordIndex!; i++, j++) {
            const progress = calculateWordProgress(transcript, i, this.duration, currentTime);
            computedValues[j] = this.#computeCurrentValue(
                progress,
                textMetrics.wordMetricsWithoutSpaces[j],
                i,
            );
        }
        this.computedValues = computedValues;
    }

    #computeCurrentValue(
        progress: number,
        metrics: TextMetricItemAPI,
        elementIndex: number = 0,
    ): number {
        if (this.interpolation === null) {
            throw new Error('TextDynamicAnimation interpolation accessed before apply');
        }
        let multi = 1;
        if (this.zigZagPerLine && elementIndex % 2 === 1) multi = -1;
        if (this.zigZagPerLine && this.zigZagPerLineStart === 'right') multi *= -1;

        const startValue = this.#inferNumber('startValue', metrics);
        const endValue = this.#inferNumber('endValue', metrics);

        return interpolateValueWithinRange(
            progress,
            {
                start: startValue * multi * this.startMulti,
                end: endValue * multi * this.endMulti,
            },
            this.interpolation,
        );
    }

    #inferNumber(value: 'startValue' | 'endValue', metrics: TextMetricItemAPI): number {
        if (typeof this[value] === 'number') {
            return this[value] as number;
        }

        if (value === 'startValue' || value === 'endValue') {
            switch (this[value]) {
                case DynamicInferredWidth.maxWidth:
                case DynamicInferredWidth.width:
                case DynamicInferredWidth.gaplessWidth:
                    return metrics.bounds.width;

                case DynamicInferredHeight.maxHeight:
                case DynamicInferredHeight.height:
                    return metrics.bounds.height;

                // These are unsupported
                case DynamicInferredHeight.heightWithoutDescent:
                case DynamicInferredHeight.gapless:
                default:
                    throw new Error(`Cannot infer value for unsupported dynamic value "${value}"`);
            }
        }

        throw new Error(`Cannot infer invalid dynamic value "${value}"`);
    }
}

/**
 * Calculate the progress of a word within a transcript based on its start time, the current time and the duration of
 * the animation. We want the given word to be at 0% progress at the start of the animation and 100% at the end. Here
 * the end of the animation represents the start time of the spoken word. In other words, the word is at 100% progress
 * when it is spoken.
 */
export const calculateWordProgress = (
    transcript: Transcript,
    wordIndex: number,
    duration: number,
    currentTime: number,
) => {
    const completionTime = transcript.words[wordIndex].startTime;
    if (currentTime >= completionTime) {
        return 1;
    }
    const startTime = completionTime - duration;
    if (currentTime <= startTime) {
        return 0;
    }
    return calculateProgress(currentTime, startTime, completionTime);
};

export const remapValue = (value: number, min: number, max: number) => {
    const range = max - min;
    return value * range + min;
};

const calculateProgress = (value: number, min: number, max: number) => {
    if (min === max) {
        throw new Error('Cannot calculate progress if min and max value are equal');
    }
    const range = max - min;
    return (value - min) / range;
};

export const interpolateValueWithinRange = (
    currentValue: number,
    range: { start: number; end: number },
    interpolation: string,
) => {
    if (isNaN(currentValue)) {
        throw new Error('Cannot interpolate current value of NaN');
    }
    const interpolationPreset = Config.INTERPOLATION_PRESET_DATA[interpolation];
    const curve = BezierCurve.fromDescription(interpolationPreset);
    curve.percentage = currentValue;
    return remapValue(curve.value, range.start, range.end);
};
